import {PartyType} from '@sollapay/enums';
import {
  CorpIdField,
  FieldDatePicker,
  FieldInput,
  FormSectionHeading,
  Icon,
  NationalIdField,
  PhoneNumberField
} from '@sollapay/ui/components';
import {generateCompanyId, generateNationalId} from '@sollapay/utils';
import {Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FullNameField} from '@/components';
import {PepSection, PepToggleField, usePepParties} from '@/components/pep';
import {ControlledAddressField} from '@/features/address';
import {AiFlashWrapper, useAiFillFlash, useFieldAiProps} from '@/features/ai-extraction';
import {BuyerKycDocumentUpload, TaxResidencyFields} from '@/features/buyers/components';
import {getNestedFieldError} from '@/features/buyers/utils';
import {
  generateHebrewCorpName,
  generateHebrewPersonName,
  generateIncorporationDate,
  generateIsraeliAddress,
  generatePhoneNumber,
  withDebugFieldChange,
  withDebugSetValue
} from '@/features/debug-test-data';
import {useCurrentTrustAccount} from '@/features/trust-accounts/providers';
import {
  companyIdRule,
  emailRule,
  fullNameRule,
  futureDateRule,
  nationalIdRule,
  phoneRule
} from '@/lib/validation';

import type {AiSuggestion} from '@/features/ai-extraction';
import type {CreateBuyerFormValues} from '@/features/buyers/validation';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {FC, ReactNode} from 'react';
import type {UseFormReturn} from 'react-hook-form';

export interface BuyerCorporateFieldsProps {
  form: UseFormReturn<CreateBuyerFormValues>;
  index: number;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  markFieldModified: (field: string) => void;
  uploadStatus?: ReactNode;
}

export const BuyerCorporateFields: FC<BuyerCorporateFieldsProps> = ({
  form,
  index,
  aiFields,
  isProcessing,
  markFieldModified,
  uploadStatus
}) => {
  const {t} = useTranslation('trustAccounts');
  const {
    register,
    control,
    formState: {errors}
  } = form;
  const prefix = 'view.buyers.addBuyerModal.corporateDetails';
  const setDebugValue = form.setValue as DebugSetValue;
  const {id: trustAccountId} = useCurrentTrustAccount();
  const buyerKey = form.getValues(`buyers.${index}.person.buyerKey`);

  const fp = (name: string) => `buyers.${index}.organization.${name}`;
  const fieldAi = useFieldAiProps({
    aiFields,
    isProcessing,
    markFieldModified,
    getValue: form.getValues
  });
  const isFlashing = useAiFillFlash(aiFields);

  const orgErrors = errors.buyers?.[index]?.organization;
  const signatoryErrors = errors.buyers?.[index]?.signatories?.[0];
  const pepParties = usePepParties({
    control,
    isCorporate: true,
    pepPath: `buyers.${index}.organization.pep`,
    requirePartiesWhenPep: true
  });

  return (
    <div className="flex flex-col gap-6">
      <BuyerKycDocumentUpload
        partyType={PartyType.ORGANIZATION}
        trustAccountId={trustAccountId}
        metadata={{buyerKey}}
        onDocumentsLoaded={documentIds =>
          form.setValue(`buyers.${index}.organization.companyDocumentIds`, documentIds, {
            shouldDirty: true
          })
        }
      />
      {uploadStatus}

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper field={fp('companyName')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <FieldInput
              label={t(`${prefix}.companyName`)}
              placeholder={t(`${prefix}.companyNamePlaceholder`)}
              error={orgErrors?.companyName?.message}
              required
              startAdornment={<Icon name="building" className="size-4" />}
              cornerBadge={withDebugSetValue(
                ai.aiIndicator,
                setDebugValue,
                fp('companyName'),
                generateHebrewCorpName,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              {...register(
                `buyers.${index}.organization.companyName` as `buyers.0.organization.companyName`,
                {
                  required: t('common:fields.fullName.required_organization'),
                  onChange: () => ai.onAiChange()
                }
              )}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper field={fp('incorporationNumber')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <Controller
              name={
                `buyers.${index}.organization.incorporationNumber` as `buyers.0.organization.incorporationNumber`
              }
              control={control}
              rules={companyIdRule(t)}
              render={({field}) => (
                <CorpIdField
                  label={t(`${prefix}.companyNumber`)}
                  placeholder={t(`${prefix}.companyNumberPlaceholder`)}
                  error={orgErrors?.incorporationNumber?.message}
                  required
                  cornerBadge={withDebugFieldChange(
                    ai.aiIndicator,
                    field.onChange,
                    generateCompanyId,
                    ai.onAiChange
                  )}
                  isLoading={ai.aiGenerating}
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    ai.onAiChange();
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
          )}
        </AiFlashWrapper>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper field={fp('address')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <ControlledAddressField
              control={control}
              name={`buyers.${index}.organization.address` as `buyers.0.organization.address`}
              label={t(`${prefix}.registeredAddress`)}
              error={orgErrors?.address?.message}
              cornerBadge={withDebugSetValue(
                ai.aiIndicator,
                setDebugValue,
                fp('address'),
                generateIsraeliAddress,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              onAiChange={ai.onAiChange}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper field={fp('incorporationDate')} fieldAi={fieldAi} isFlashing={isFlashing}>
          {ai => (
            <Controller
              name={
                `buyers.${index}.organization.incorporationDate` as `buyers.0.organization.incorporationDate`
              }
              control={control}
              rules={{
                required: t('common:fields.incorporationDate.required'),
                ...futureDateRule(t)
              }}
              render={({field}) => (
                <FieldDatePicker
                  label={t(`${prefix}.incorporationDate`)}
                  placeholder={t(`${prefix}.incorporationDatePlaceholder`)}
                  error={orgErrors?.incorporationDate?.message}
                  required
                  disableFuture
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    ai.onAiChange();
                  }}
                  onBlur={field.onBlur}
                  cornerBadge={withDebugFieldChange(
                    ai.aiIndicator,
                    field.onChange,
                    generateIncorporationDate,
                    ai.onAiChange
                  )}
                  isLoading={ai.aiGenerating}
                />
              )}
            />
          )}
        </AiFlashWrapper>
      </div>

      <TaxResidencyFields
        control={control}
        taxResidencyName={
          `buyers.${index}.organization.taxResidency` as `buyers.0.organization.taxResidency`
        }
        taxResidencyCountryName={
          `buyers.${index}.organization.taxResidencyCountry` as `buyers.0.organization.taxResidencyCountry`
        }
        taxResidencyError={orgErrors?.taxResidency?.message}
        taxResidencyCountryError={orgErrors?.taxResidencyCountry?.message}
        i18nPrefix={prefix}
        extra={
          <PepToggleField
            control={form.control}
            isCorporate
            pepPath={`buyers.${index}.organization.pep`}
            replace={pepParties.replace}
          />
        }
      />

      <FormSectionHeading
        title={t(`${prefix}.signatoryTitle`)}
        description={t(`${prefix}.signatorySubtitle`)}
      />

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper
          field={`buyers.${index}.signatories.0.fullName`}
          fieldAi={fieldAi}
          isFlashing={isFlashing}
        >
          {ai => (
            <FullNameField
              label={t(`${prefix}.signatoryName`)}
              error={signatoryErrors?.fullName?.message}
              required
              cornerBadge={withDebugSetValue(
                ai.aiIndicator,
                setDebugValue,
                `buyers.${index}.signatories.0.fullName`,
                generateHebrewPersonName,
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
              {...register(
                `buyers.${index}.signatories.0.fullName` as `buyers.0.signatories.0.fullName`,
                {
                  ...fullNameRule(t),
                  onChange: () => ai.onAiChange()
                }
              )}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper
          field={`buyers.${index}.signatories.0.nationalId`}
          fieldAi={fieldAi}
          isFlashing={isFlashing}
        >
          {ai => (
            <Controller
              name={
                `buyers.${index}.signatories.0.nationalId` as `buyers.0.signatories.0.nationalId`
              }
              control={control}
              rules={nationalIdRule(t)}
              render={({field}) => (
                <NationalIdField
                  label={t(`${prefix}.signatoryNationalId`)}
                  placeholder={t(`${prefix}.signatoryNationalIdPlaceholder`)}
                  error={signatoryErrors?.nationalId?.message}
                  required
                  cornerBadge={withDebugFieldChange(
                    ai.aiIndicator,
                    field.onChange,
                    generateNationalId,
                    ai.onAiChange
                  )}
                  isLoading={ai.aiGenerating}
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    ai.onAiChange();
                  }}
                  onBlur={field.onBlur}
                />
              )}
            />
          )}
        </AiFlashWrapper>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <AiFlashWrapper
          field={`buyers.${index}.signatories.0.email`}
          fieldAi={fieldAi}
          isFlashing={isFlashing}
        >
          {ai => (
            <FieldInput
              label={t(`${prefix}.signatoryEmail`)}
              placeholder={t(`${prefix}.signatoryEmailPlaceholder`)}
              error={signatoryErrors?.email?.message}
              type="email"
              required
              startAdornment={<Icon name="mail" className="size-4" />}
              cornerBadge={ai.aiIndicator}
              isLoading={ai.aiGenerating}
              {...register(
                `buyers.${index}.signatories.0.email` as `buyers.0.signatories.0.email`,
                {
                  ...emailRule(t),
                  onChange: () => ai.onAiChange()
                }
              )}
            />
          )}
        </AiFlashWrapper>
        <AiFlashWrapper
          field={`buyers.${index}.signatories.0.phone`}
          fieldAi={fieldAi}
          isFlashing={isFlashing}
        >
          {ai => (
            <Controller
              name={`buyers.${index}.signatories.0.phone` as `buyers.0.signatories.0.phone`}
              control={control}
              rules={phoneRule(t)}
              render={({field}) => (
                <PhoneNumberField
                  label={t(`${prefix}.signatoryPhone`)}
                  placeholder={t(`${prefix}.signatoryPhonePlaceholder`)}
                  error={signatoryErrors?.phone?.message}
                  required
                  cornerBadge={withDebugFieldChange(
                    ai.aiIndicator,
                    field.onChange,
                    generatePhoneNumber,
                    ai.onAiChange
                  )}
                  isLoading={ai.aiGenerating}
                  value={field.value}
                  onChange={value => {
                    field.onChange(value);
                    ai.onAiChange();
                  }}
                  onBlur={field.onBlur}
                  ref={field.ref}
                />
              )}
            />
          )}
        </AiFlashWrapper>
      </div>

      <PepSection
        control={form.control}
        register={form.register}
        isCorporate
        pepPath={`buyers.${index}.organization.pep`}
        fe={(key: string) => getNestedFieldError(form.formState.errors, key)}
        fields={pepParties.fields}
        append={pepParties.append}
        remove={pepParties.remove}
      />
    </div>
  );
};
